import React, { useRef, useEffect } from 'react';
import styles from './Calculator.module.css';
import type { Option, Currency, DefaultOption, SelectedOption } from '../../types';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useLocale } from '../../contexts/LocaleContext';
import { formatPrice } from '../../utils/calculations';
import { formatDeadlineLocalized, getLocalizedString, getLocalizedStringArray } from '../../utils/localization';
import { t } from '../../locales/translations';

interface CalculatorProps {
  selectedOptions: Option[];
  selectedOptionsWithQuantities?: SelectedOption[];
  defaultOptions: DefaultOption[];
  totalPrices: {
    [Currency.USD]: number;
    [Currency.EUR]: number;
    [Currency.KZT]: number;
    [Currency.RUB]: number;
  };
  projectDeadline: number;
  hourlyOptions: Option[];
  onClearSelections: () => void;
}

export const Calculator: React.FC<CalculatorProps> = ({
  selectedOptions,
  selectedOptionsWithQuantities = [],
  defaultOptions,
  totalPrices,
  projectDeadline,
  hourlyOptions,
  onClearSelections
}) => {
  const { selectedCurrency } = useCurrency();
  const { selectedLocale } = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when hourly options are added
  useEffect(() => {
    if (hourlyOptions.length > 0 && contentRef.current) {
      // Use a small timeout to ensure the DOM has updated
      setTimeout(() => {
        contentRef.current?.scrollTo({
          top: contentRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [hourlyOptions.length]);

  // Calculate total count including quantities
  const totalItemCount = selectedOptionsWithQuantities.reduce(
    (sum, item) => sum + item.quantity,
    0
  ) || selectedOptions.length;

  return (
    <div className={styles.calculator}>
      {/* Fixed Header with Title */}
      <div className={styles.calculator_header}>
        <h2 className={styles.title}>{t('costCalculator', selectedLocale)}</h2>
      </div>

      {/* Scrollable Content */}
      <div ref={contentRef} className={styles.calculator_content}>
        <div className={styles.metrics}>
          <div className={styles.metric}>
            <span className={styles.label}>{t('selectedOptions', selectedLocale)}</span>
            <span className={styles.value}>{totalItemCount}</span>
          </div>

          <div className={styles.metric}>
            <span className={styles.label}>{t('totalPrice', selectedLocale)}</span>
            <span className={styles.price}>{formatPrice(totalPrices[selectedCurrency], selectedCurrency)}</span>
          </div>

          <div className={styles.metric}>
            <span className={styles.label}>{t('projectTimeline', selectedLocale)}</span>
            <span className={styles.deadline}>{formatDeadlineLocalized(projectDeadline, selectedLocale)}</span>
          </div>
        </div>

        {defaultOptions.length > 0 && (
          <div className={styles.defaultSection}>
            <h3 className={styles.defaultSectionTitle}>
              {t('includedServices', selectedLocale)}
              <span className={styles.defaultBadge}>{t('alwaysIncluded', selectedLocale)}</span>
            </h3>
            <div className={styles.defaultList}>
              {defaultOptions.map((option) => {
                const description = getLocalizedString(option.description, selectedLocale);
                const tags = getLocalizedStringArray(option.subtasks, selectedLocale);

                return (
                  <div key={option.id} className={styles.defaultItem}>
                    <div className={styles.defaultHeader}>
                      <span className={styles.defaultLabel}>{getLocalizedString(option.title, selectedLocale)}</span>
                      {option.price && (
                        <span className={styles.defaultPrice}>
                          {formatPrice(option.price[selectedCurrency], selectedCurrency)}
                        </span>
                      )}
                    </div>
                    {description && <div className={styles.defaultDescription}>{description}</div>}
                    {tags.length > 0 && (
                      <div className={styles.defaultTags}>
                        {tags.map((tag, index) => (
                          <span key={index} className={styles.defaultTag}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Show selected options with quantities */}
        {selectedOptionsWithQuantities.length > 0 && (
          <div className={styles.selectedSection}>
            <h3 className={styles.selectedSectionTitle}>{t('selectedItems', selectedLocale)}</h3>
            <div className={styles.selectedList}>
              {selectedOptionsWithQuantities.map((item) => {
                const option = item.option;
                const showQuantity = option.allow_multiple_instances && item.quantity > 1;
                const unitPrice = option.price ? option.price[selectedCurrency] : 0;
                const totalPrice = unitPrice * item.quantity;

                return (
                  <div key={option.id} className={styles.selectedItem}>
                    <div className={styles.selectedItemInfo}>
                      <span className={styles.selectedLabel}>
                        {getLocalizedString(option.title, selectedLocale)}
                        {showQuantity && (
                          <span className={styles.quantityIndicator}> ×{item.quantity}</span>
                        )}
                      </span>
                    </div>
                    {option.price && (
                      <span className={styles.selectedPrice}>
                        {showQuantity ? (
                          <span className={styles.priceWithQuantity}>
                            <span className={styles.unitPrice}>
                              {formatPrice(unitPrice, selectedCurrency)} × {item.quantity}
                            </span>
                            <span className={styles.totalItemPrice}>
                              = {formatPrice(totalPrice, selectedCurrency)}
                            </span>
                          </span>
                        ) : (
                          formatPrice(unitPrice, selectedCurrency)
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {hourlyOptions.length > 0 && (
          <div className={styles.hourlySection}>
            <h3 className={styles.hourlySectionTitle}>{t('hourlyCosts', selectedLocale)}</h3>
            <div className={styles.hourlyList}>
              {hourlyOptions.map((option) => {
                // Check if this option has quantity
                const withQuantity = selectedOptionsWithQuantities.find(item => item.option.id === option.id);
                const quantity = withQuantity ? withQuantity.quantity : 1;
                const showQuantity = option.allow_multiple_instances && quantity > 1;

                return (
                  <div key={option.id} className={styles.hourlyItem}>
                    <span className={styles.hourlyLabel}>
                      {getLocalizedString(option.title, selectedLocale)}
                      {showQuantity && (
                        <span className={styles.quantityIndicator}> ×{quantity}</span>
                      )}
                    </span>
                    {option.price && (
                      <span className={styles.hourlyPrice}>
                        +{formatPrice(option.price[selectedCurrency], selectedCurrency)}/{t('hour', selectedLocale)}
                        {showQuantity && (
                          <span className={styles.hourlyQuantityNote}> ({quantity} units)</span>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Fixed Footer with Clear Button */}
      <div className={styles.calculator_footer}>
        <button
          className={styles.clearButton}
          onClick={onClearSelections}
          disabled={selectedOptions.length === 0}
        >
          {t('clearAll', selectedLocale)}
        </button>
      </div>
    </div>
  );
};